import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Calendar, MapPin, ChevronRight, Loader2 } from 'lucide-react';
import { getAnonymousId, trackRecommendationClick } from '@/hooks/useTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "You Might Like" recommendation component
 * Displays personalized event recommendations based on user behavior
 * 
 * @param {Object} props
 * @param {string} props.currentEventId - Current event ID to exclude
 * @param {string} props.variant - 'sidebar' (default) or 'inline'
 * @param {number} props.limit - Number of recommendations (default: 4)
 * @param {string} props.source - Source context for tracking
 */
export default function YouMightLike({ 
  currentEventId, 
  variant = 'sidebar', 
  limit = 4,
  source = 'event_detail'
}) {
  const [recommendations, setRecommendations] = useState([]);
  const [profileSummary, setProfileSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const anonId = getAnonymousId();
        
        const response = await axios.get(
          `${BACKEND_URL}/api/public/recommendations/${currentEventId}`,
          {
            params: { limit },
            headers: {
              'X-Anonymous-ID': anonId
            }
          }
        );
        
        setRecommendations(response.data.recommendations || []);
        setProfileSummary(response.data.profileSummary);
        setError(false);
      } catch (err) {
        console.error('Failed to fetch recommendations:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    if (currentEventId) {
      fetchRecommendations();
    }
  }, [currentEventId, limit]);

  const handleClick = (recommendation) => {
    trackRecommendationClick(recommendation, source);
  };

  // Don't render if no recommendations or error
  if (error || (!loading && recommendations.length === 0)) {
    return null;
  }

  if (variant === 'inline') {
    return (
      <InlineVariant 
        recommendations={recommendations}
        profileSummary={profileSummary}
        loading={loading}
        onRecommendationClick={handleClick}
      />
    );
  }

  // Sidebar variant (default)
  return (
    <Card className="bg-gradient-to-br from-purple-50 to-orange-50 border-purple-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center text-gray-800">
          <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
          Events You Might Like
        </CardTitle>
        {profileSummary?.topSectors?.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            Based on your interest in {profileSummary.topSectors.slice(0, 2).join(' & ')}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : (
          recommendations.map((rec) => (
            <RecommendationCard 
              key={rec.eventId} 
              recommendation={rec} 
              onClick={() => handleClick(rec)}
              compact
            />
          ))
        )}
        
        <Link 
          to="/events"
          className="flex items-center justify-center text-sm text-purple-600 hover:text-purple-700 font-medium pt-2"
        >
          Browse All Events
          <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </CardContent>
    </Card>
  );
}

/**
 * Inline variant for use in content areas
 */
function InlineVariant({ recommendations, profileSummary, loading, onRecommendationClick }) {
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl p-6 my-8">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl p-6 my-8">
      <div className="flex items-center mb-4">
        <Sparkles className="w-6 h-6 text-purple-600 mr-2" />
        <h3 className="text-xl font-bold text-gray-800">More Events You Might Like</h3>
      </div>
      
      {profileSummary?.topSectors?.length > 0 && (
        <p className="text-gray-600 mb-6">
          Based on your interest in{' '}
          <span className="font-medium">{profileSummary.topSectors.slice(0, 2).join(' and ')}</span>
        </p>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {recommendations.map((rec) => (
          <RecommendationCard 
            key={rec.eventId} 
            recommendation={rec}
            onClick={() => onRecommendationClick(rec)}
          />
        ))}
      </div>
      
      <div className="text-center mt-6">
        <Link 
          to="/events"
          className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
        >
          View All Events
          <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
    </div>
  );
}

/**
 * Individual recommendation card
 */
function RecommendationCard({ recommendation, onClick, compact = false }) {
  const { eventName, city, venueName, dates, reason, url, sectorTags } = recommendation;
  
  if (compact) {
    return (
      <Link 
        to={url}
        onClick={onClick}
        className="block p-3 bg-white rounded-lg border border-gray-100 hover:border-purple-300 hover:shadow-md transition-all group"
      >
        <h4 className="font-medium text-gray-900 group-hover:text-purple-700 text-sm line-clamp-2">
          {eventName}
        </h4>
        <div className="flex items-center text-xs text-gray-500 mt-1">
          <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">{city}</span>
        </div>
        <div className="flex items-center text-xs text-gray-500 mt-1">
          <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">{dates?.display || 'TBC'}</span>
        </div>
        <p className="text-xs text-purple-600 mt-2 font-medium">
          {reason}
        </p>
      </Link>
    );
  }
  
  return (
    <Link 
      to={url}
      onClick={onClick}
      className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
    >
      <h4 className="font-semibold text-gray-900 group-hover:text-purple-700 mb-2 line-clamp-2">
        {eventName}
      </h4>
      
      <div className="space-y-1 mb-3">
        <div className="flex items-center text-sm text-gray-600">
          <MapPin className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
          <span className="truncate">{venueName}, {city}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
          <span>{dates?.display || 'Dates TBC'}</span>
        </div>
      </div>
      
      {sectorTags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {sectorTags.slice(0, 2).map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}
      
      <p className="text-sm text-purple-600 font-medium flex items-center">
        {reason}
        <ChevronRight className="w-4 h-4 ml-1" />
      </p>
    </Link>
  );
}
